/* eslint-disable camelcase */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { memo, useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import classNames from 'classnames';
import BaseModal from '../../../components/modals/BaseModal/BaseModal';
import { ArrowLeftIcon, BadgeIcon, BulbIcon, ErrorIcon } from '../../../components/icons';
import { useGetAvailableBundleSlotsMutation, useGuaranteeBundleMutation } from '../../../redux/guaranteeShifts';
import LoadingSpinner from '../../../components/spinner/LoadingSpinner';

import './GuaranteeBundleModal.scss';

const GuaranteeBundleModal = ({
	bundleSlots,
	onSaveChanges = () => {},
	onCancel = () => {},
	bundleSelectedSlot = {},
	onError,
	fromGlobal = false,
	setIsLoading
}) => {
	const [acknowledgement, setAcknowledgment] = useState(false);
	const [tableViewExpanded, setTableViewExpanded] = useState(false);
	const [slots, setSlots] = useState([]);
	const [showSpinner, setShowSpinner] = useState(false);
	const [guaranteeBundle] = useGuaranteeBundleMutation();
	const [getAvailableBundleSlots] = useGetAvailableBundleSlotsMutation();

	useEffect(() => {
		(async () => {
			if (!bundleSlots) {
				try {
					setShowSpinner(true);
					const res = await getAvailableBundleSlots(bundleSelectedSlot.contract_id).unwrap();
					if (!res.length && onError) {
						onError(null, 'This bundle cannot be guaranteed as the selected shift has already started.', true);
					}
					setSlots(res);
				} catch (e) {
					if (onError) {
						onError(e);
					}
				} finally {
					setShowSpinner(false);
				}
			} else {
				if (!bundleSlots.length && onError) {
					onError(null, 'This bundle cannot be guaranteed as the selected shift has already started.', true);
				}
				setSlots(bundleSlots);
			}
		})();
	}, [bundleSelectedSlot]);

	const slotsToUpdate = useMemo(() => {
		return slots?.reduce((acc, slot) => {
			acc[slot.slot_id] = slot.is_guaranteed;
			return acc;
		}, {});
	}, [slots]);
	const isGuaranteed = useMemo(() => slots?.every((slot) => slot.is_guaranteed), [slots]);

	const contractId = bundleSelectedSlot.contract_id;
	const weeksCount = useMemo(() => {
		const firstShift = slots?.[0];
		const lastShift = slots?.[slots.length - 1];
		return slots.length > 1
			? Math.ceil(
					moment.duration(moment.utc(lastShift?.raw_start_time).diff(moment.utc(firstShift?.raw_start_time))).asWeeks()
			  )
			: 1;
	}, [slots]);

	const onGuaranteeBundle = async (newGuaranteedStatus) => {
		if (setIsLoading) {
			setIsLoading(true);
		}

		try {
			await guaranteeBundle({
				contractId,
				body: {
					guaranteed: newGuaranteedStatus,
					slots: slotsToUpdate,
					acknowledgement,
					currentSlotId: bundleSelectedSlot.slot_id
				}
			}).unwrap();
			onSaveChanges(newGuaranteedStatus);
		} catch (e) {
			if (onError) {
				onError(e);
			}
		} finally {
			if (setIsLoading) {
				setIsLoading(false);
			}
		}
	};

	let title = !isGuaranteed ? 'Guarantee Bundle?' : 'Remove Bundle guarantee?';

	if (fromGlobal && isGuaranteed) {
		title = 'This Bundle is already guaranteed. Remove Bundle guarantee?';
	}

	const guaranteeDetails = isGuaranteed ? (
		<div className="guarantee-section remove-guarantee">
			<div className="section-icon">
				<ErrorIcon />
			</div>
			<div className="section-text">
				<p>
					<b>Removing the Guaranteed Shifts® means you’re missing out</b>
				</p>
				<p>Guaranteed Bundles&trade; achieve 98% arrival rates, boosting nurse and patient satisfaction.</p>
			</div>
		</div>
	) : (
		<div className="guarantee-section">
			<div className="section-icon">
				<BulbIcon />
			</div>
			<div className="section-text">
				<p>
					<b>Why guarantee a bundle?</b>
				</p>
				<p>Guaranteed Bundles&trade; achieve 98% arrival rates, boosting nurse and patient satisfaction.</p>
			</div>
		</div>
	);

	const expandTableViewButton = (
		<div className="mobile-button-container">
			{!tableViewExpanded ? (
				<button
					type="button"
					className="expand-table-buttons secondary-button"
					onClick={() => setTableViewExpanded(!tableViewExpanded)}
				>
					Expand Table View
				</button>
			) : (
				<button
					type="button"
					className="expand-table-buttons primary-button"
					onClick={() => setTableViewExpanded(!tableViewExpanded)}
				>
					<ArrowLeftIcon /> Shrink Table View
				</button>
			)}
		</div>
	);

	const acknowledgmentText = !isGuaranteed
		? 'I acknowledge that by guaranteeing this Bundle, I am committing to pay for the professional even if I choose to cancel any of these shifts after the Bundle is claimed.'
		: 'I acknowledge that by removing the Bundle guarantee, I am reducing the chance of this Bundle being claimed by a professional.';

	return !showSpinner ? (
		<BaseModal
			key="guarantee-bundle-modal"
			title={title}
			className={classNames('guarantee-bundle-modal', { 'is-guaranteeing ': !isGuaranteed })}
			primaryActionLabel={!isGuaranteed ? 'Guarantee Bundle' : 'Remove Bundle guarantee'}
			onPrimaryAction={() => onGuaranteeBundle(!isGuaranteed)}
			primaryActionDisabled={!acknowledgement}
			secondaryActionLabel="Cancel"
			onSecondaryAction={onCancel}
			onHide={onCancel}
			confirmNewModal
		>
			<div className="info-header-section">
				<p>Facility</p>
				<p>
					<b>{bundleSelectedSlot.facility_name}</b>
				</p>
			</div>
			<div className={classNames('info-header-container', { hidden: !tableViewExpanded })}>
				<div className="info-header-section">
					<p>Bundle length</p>
					<p>
						<b>
							{weeksCount} {weeksCount > 1 ? 'weeks' : 'week'}
						</b>
					</p>
				</div>
				<div className="info-header-section">
					<p>Number of shifts</p>
					<p>
						<b>{slots.length}</b>
					</p>
				</div>
				<div className="info-header-section">
					<p>Shift type</p>
					<p>
						<b>{bundleSelectedSlot.resource_type}</b>
					</p>
				</div>
			</div>

			<div className="slots-table">
				<div className={classNames('table-container', { expanded: tableViewExpanded })}>
					<table>
						<thead>
							<tr>
								<th>Date</th>
								<th>Shift time</th>
								<th>Unit</th>
								<th>Guarantee</th>
							</tr>
						</thead>
						<tbody>
							{slots.length > 0 ? (
								slots.map((slot) => {
									return (
										<tr key={`row-${slot.slot_id}`}>
											<td>
												<b>{slot.shift_day.slice(0, 3)}</b>, {moment.utc(slot.raw_start_time).format('MM/DD')}
											</td>
											<td>
												{slot.start_time} - {slot.end_time}
											</td>
											<td>{slot.unit_name || '-'}</td>
											<td className="guarantee-icon">{slot.is_guaranteed === true ? <BadgeIcon /> : ''}</td>
										</tr>
									);
								})
							) : (
								<div className="no-data">
									<p>No Data</p>
								</div>
							)}
						</tbody>
					</table>
				</div>
			</div>

			{expandTableViewButton}
			{guaranteeDetails}

			<div className={classNames('aknowledgement-section', { hidden: tableViewExpanded })}>
				<label className="acnowledgement-label">
					<input
						type="checkbox"
						className="custom-checkbox-control"
						value={acknowledgement}
						checked={acknowledgement}
						onChange={() => setAcknowledgment(!acknowledgement)}
					/>
					{acknowledgmentText}
				</label>
			</div>
		</BaseModal>
	) : (
		<div className="spinner-wrapper">
			<LoadingSpinner key="loading-spinner" />
		</div>
	);
};

export default memo(GuaranteeBundleModal);
